import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { ExchangeCodeForSession } from '@/modules/auth/use-cases'
import { validateRedirectUrl } from '@/modules/auth/ui/validate-redirect'

export type UseAuthCallbackPageProps = {
  onExchangeCodeForSession: ExchangeCodeForSession
}

export type UseAuthCallbackPageResult = {
  message: string
}

function appendOrgId(target: string, orgId: string | null): string {
  if (!orgId) return target
  const separator = target.includes('?') ? '&' : '?'
  return `${target}${separator}org_id=${encodeURIComponent(orgId)}`
}

export function useAuthCallbackPage({
  onExchangeCodeForSession,
}: UseAuthCallbackPageProps): UseAuthCallbackPageResult {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hasHandledRef = useRef<boolean>(false)

  useEffect(() => {
    if (hasHandledRef.current) return
    hasHandledRef.current = true

    const error = searchParams.get('error')
    const code = searchParams.get('code')
    const next = searchParams.get('next') ?? searchParams.get('redirect_to')
    const orgId = searchParams.get('org_id')

    if (error) {
      navigate(`/auth/auth-code-error?error=${encodeURIComponent(error)}`, { replace: true })
      return
    }

    if (!code) {
      navigate('/auth/auth-code-error', { replace: true })
      return
    }

    const run = async () => {
      try {
        await onExchangeCodeForSession(code)
        const safeNext = validateRedirectUrl(next) ?? '/dashboard'
        const target = appendOrgId(safeNext, orgId)
        navigate(target, { replace: true })
      } catch {
        navigate('/auth/auth-code-error', { replace: true })
      }
    }

    void run()
  }, [navigate, onExchangeCodeForSession, searchParams])

  return {
    message: 'Signing you in…',
  }
}
